import { Home, Key, TrendingUp, Search, FileText, Handshake, LucideIcon } from "lucide-react";

export interface Service {
  slug: string;
  icon: LucideIcon;
  title: string;
  shortDescription: string;
  fullDescription: string;
  features: string[];
  process: { step: number; title: string; description: string }[];
}

export const services: Service[] = [
  {
    slug: "property-search",
    icon: Search,
    title: "Property Search",
    shortDescription: "Access exclusive off-market listings and find your perfect home with personalized guidance.",
    fullDescription: "Finding your dream home in Chicago's competitive market requires more than browsing online listings. With access to exclusive off-market properties and a deep network of industry connections, I help you discover opportunities others miss. My personalized search process ensures every property you view aligns with your lifestyle, budget, and long-term goals.",
    features: [
      "Access to exclusive off-market and pre-market listings",
      "Personalized property matching based on your criteria",
      "Neighborhood expertise across all Chicago areas",
      "Virtual and in-person property tours",
      "Detailed property analysis and comparisons",
      "Real-time market updates and new listing alerts"
    ],
    process: [
      { step: 1, title: "Initial Consultation", description: "We discuss your needs, preferences, budget, and timeline to create a tailored search strategy." },
      { step: 2, title: "Curated Selection", description: "I identify properties that match your criteria, including exclusive off-market opportunities." },
      { step: 3, title: "Property Tours", description: "We visit top candidates together, and I provide insights on each property's value and potential." },
      { step: 4, title: "Decision Support", description: "I help you evaluate options and make a confident decision on your perfect home." }
    ]
  },
  {
    slug: "luxury-home-sales",
    icon: Home,
    title: "Luxury Home Sales",
    shortDescription: "Expert marketing and pricing strategies to maximize your property's value.",
    fullDescription: "Selling a luxury property requires a sophisticated approach that matches the caliber of your home. Leveraging the global reach of Sotheby's International Realty, I create bespoke marketing campaigns that showcase your property to qualified buyers worldwide. From professional staging to high-end photography and targeted digital marketing, every detail is crafted to maximize your return.",
    features: [
      "Global exposure through Sotheby's International Realty network",
      "Professional photography, videography, and virtual tours",
      "Strategic pricing based on comprehensive market analysis",
      "Targeted digital and print marketing campaigns",
      "Professional staging consultation",
      "Exclusive broker events and private showings"
    ],
    process: [
      { step: 1, title: "Property Evaluation", description: "Comprehensive assessment of your home's value, unique features, and market positioning." },
      { step: 2, title: "Marketing Strategy", description: "Custom marketing plan including professional media, staging, and targeted buyer outreach." },
      { step: 3, title: "Market Launch", description: "Strategic listing debut with maximum exposure across all channels." },
      { step: 4, title: "Negotiation & Close", description: "Expert negotiation to secure the best terms and seamless closing process." }
    ]
  },
  {
    slug: "first-time-buyers",
    icon: Key,
    title: "First-Time Buyers",
    shortDescription: "Comprehensive support navigating the buying process from start to keys.",
    fullDescription: "Buying your first home is one of life's most exciting milestones—and one of the most complex. I specialize in guiding first-time buyers through every step, demystifying the process and advocating for your interests. From understanding financing options to navigating inspections and negotiations, you'll have a trusted advisor by your side.",
    features: [
      "Step-by-step guidance through the entire buying process",
      "Mortgage lender recommendations and financing education",
      "Understanding of first-time buyer programs and incentives",
      "Property inspection coordination and guidance",
      "Clear explanation of all contracts and paperwork",
      "Post-purchase support and vendor recommendations"
    ],
    process: [
      { step: 1, title: "Buyer Education", description: "Overview of the buying process, financing options, and what to expect at each stage." },
      { step: 2, title: "Pre-Approval", description: "Connect with trusted lenders to get pre-approved and understand your budget." },
      { step: 3, title: "Home Search", description: "Find properties that fit your needs and budget with guided tours and market insights." },
      { step: 4, title: "From Offer to Keys", description: "Navigate offers, inspections, and closing with expert support every step of the way." }
    ]
  },
  {
    slug: "investment-properties",
    icon: TrendingUp,
    title: "Investment Properties",
    shortDescription: "Strategic advice on Chicago's best investment opportunities and rental markets.",
    fullDescription: "Chicago's diverse neighborhoods offer compelling investment opportunities for both new and seasoned investors. Whether you're looking for multi-family properties, condos for short-term rentals, or value-add opportunities, I provide data-driven insights on rental yields, appreciation potential, and neighborhood trends to help you build wealth through real estate.",
    features: [
      "Analysis of rental yields and cash flow projections",
      "Neighborhood investment potential assessments",
      "Multi-family and single-family investment guidance",
      "Short-term rental market insights",
      "Property management recommendations",
      "Portfolio strategy and diversification advice"
    ],
    process: [
      { step: 1, title: "Investment Goals", description: "Define your investment objectives, risk tolerance, and target returns." },
      { step: 2, title: "Market Analysis", description: "Identify neighborhoods and property types with the best investment potential." },
      { step: 3, title: "Property Evaluation", description: "Analyze specific properties for cash flow, appreciation, and ROI." },
      { step: 4, title: "Acquisition & Setup", description: "Secure the property and connect you with management resources." }
    ]
  },
  {
    slug: "market-analysis",
    icon: FileText,
    title: "Market Analysis",
    shortDescription: "In-depth neighborhood reports and pricing analysis for informed decisions.",
    fullDescription: "Making informed real estate decisions requires understanding the market dynamics at play. I provide detailed market analyses that go beyond surface-level statistics, offering insights into neighborhood trends, pricing patterns, and future developments. Whether you're buying, selling, or investing, data-driven intelligence gives you the edge.",
    features: [
      "Comprehensive neighborhood market reports",
      "Comparative market analysis for accurate pricing",
      "Historical trends and future projections",
      "Development and infrastructure impact analysis",
      "School district and amenity evaluations",
      "Customized reports for your specific needs"
    ],
    process: [
      { step: 1, title: "Define Scope", description: "Identify the specific neighborhoods, property types, or questions you need answered." },
      { step: 2, title: "Data Gathering", description: "Compile comprehensive data from multiple sources for thorough analysis." },
      { step: 3, title: "Analysis & Insights", description: "Interpret the data to uncover trends, opportunities, and considerations." },
      { step: 4, title: "Strategic Recommendations", description: "Receive actionable insights to inform your real estate decisions." }
    ]
  },
  {
    slug: "negotiation",
    icon: Handshake,
    title: "Negotiation",
    shortDescription: "Proven negotiation skills that have saved clients millions in transactions.",
    fullDescription: "In real estate, skilled negotiation can mean hundreds of thousands of dollars in savings or gains. With years of experience and a track record of successful negotiations, I advocate fiercely for my clients' interests. From initial offers to repair negotiations and final terms, my strategic approach ensures you get the best possible outcome.",
    features: [
      "Strategic offer positioning and presentation",
      "Multi-offer situation expertise",
      "Inspection and repair negotiations",
      "Contract term optimization",
      "Closing cost negotiations",
      "Creative problem-solving for complex deals"
    ],
    process: [
      { step: 1, title: "Strategy Development", description: "Understand your priorities and develop a negotiation strategy tailored to the situation." },
      { step: 2, title: "Market Positioning", description: "Position your offer or listing strategically based on market conditions." },
      { step: 3, title: "Active Negotiation", description: "Engage with the other party to advocate for your best interests." },
      { step: 4, title: "Deal Optimization", description: "Fine-tune terms and conditions to maximize value and minimize risk." }
    ]
  }
];

export const getServiceBySlug = (slug: string): Service | undefined => {
  return services.find(service => service.slug === slug);
};
